// Install necessary packages before running:
// npm install @mui/material @emotion/react @emotion/styled formik

import React, { useState } from "react";
import { Button, TextField, Grid2, Divider, Box, Typography, Stack, Alert, Avatar, AlertTitle } from "@mui/material";
import { Link as MuiLink } from '@mui/material';
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { Error } from "@mui/icons-material";
import { CheckCircleOutline, Login, CelebrationOutlined, EmailOutlined } from '@mui/icons-material';
import { AWS_LINK, DEV_LINK } from "../Static/static";


// Validation Schema for Formik
const RegisterSchema = Yup.object().shape({
  fullname: Yup.string().required("Fullname is required"),
  email: Yup.string().email("Invalid email format").required("Email is required"),
  password: Yup.string().min(6, "Password must be at least 6 characters").required("Password is required"),
  password_confirm: Yup.string().min(6, "Password must be at least 6 characters").required("Password is required"),
  organization_name: Yup.string().required("Organization name is required"),
  address: Yup.string().required("Address"),
  zip_code: Yup.string().required("Zip code is required"),
  state: Yup.string().required("State is required"),
  city: Yup.string().required("City is required."),
  code: Yup.string().required("Register code required.")
});

const initialValues = {
    email: null,
    password: null,
    organization_name: null,
    address: null,
    zip_code: null,
    state: null,
    city: null,
    code: null,
    fullname: null
}

export default function RegisterPage(){
    const [error, setError] = useState(null);
    const [completed, setCompleted] = useState(false);
    const handleSubmit = async (values) => {
      if (values.password != values.password_confirm) {
          console.log("Passwords do not match try again.");
          setError("Passwords do not match.");
          return;
      }
      setError(null);
      let payload = {...values}
      let json_data = JSON.stringify(payload);
      try {
          const LINK = process.env.NODE_ENV === "production" ? AWS_LINK : DEV_LINK;
          let response = await axios.post(LINK + "/create_organization", json_data);
          if (response.data.status == "OK" && response.data.organization_id) {
              setCompleted(true);
              return;
          } else {
              setCompleted(false);
              setError('Unable to create organization');
              return;
          }
      }
      catch (error) {
          setCompleted(false);
          setError('Unable to create organization');
          return;
      }
    };

    return (
      <Box
        sx={{
          width: "100%",
          maxWidth: 450,
          margin: "auto",
          mt: 8,
          p: 4,
          boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.08)',
          borderRadius: 2,
          bgcolor: 'background.paper',
          position: 'relative',
          '&:before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 4,
            bgcolor: 'primary.main',
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8
          }
        }}
      >
        <Box textAlign="center" mb={3}>
          <Typography
            fontWeight={700}
            variant="h5"
            sx={{
              color: 'primary.main',
              letterSpacing: '0.5px',
              mb: 1
            }}
          >
            Create Root Account
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Register a root user to manage administrative processes and tutors
          </Typography>
        </Box>

        {error && (
          <Box sx={{ mb: 3 }}>
            <Alert
              icon={<Error />}
              severity="error"
              sx={{ borderRadius: 2, boxShadow: 'none' }}
            >
              {error}
            </Alert>
          </Box>
        )}

        {completed ? (
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '45vh',
            p: 2,
          }}>
            <Box sx={{
              position: 'relative',
              maxWidth: 500,
              width: '100%',
              p: 4,
              pt: 8,
              borderRadius: 4,
              textAlign: 'center',
              boxShadow: 3,
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '40%',
                opacity: 0.05,
                borderTopLeftRadius: 4,
                borderTopRightRadius: 4,
                zIndex: 0
              }
            }}>
              <Avatar sx={{
                position: 'absolute',
                top: -40,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 80,
                height: 80,
                bgcolor: 'success.light',
                boxShadow: 3,
                zIndex: 1
              }}>
                <CheckCircleOutline sx={{ fontSize: 48 }} color="success" />
              </Avatar>

              <Box sx={{ position: 'relative', zIndex: 1 }}>
                <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
                  Welcome Aboard!
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Your account has been successfully created. Please log in with the credentials used.
                </Typography>

                <Box sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  my: 2,
                  px: 3,
                  py: 1.5,
                  bgcolor: 'action.hover',
                  borderRadius: 2
                }}>
                  <EmailOutlined color="action" fontSize="small" />
                  <Typography variant="body2" color="text.secondary">
                    Please verify your email to get started
                  </Typography>
                </Box>

                <Divider sx={{ my: 2, width: '80%', mx: 'auto' }} />

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 3 }}>
                  <CelebrationOutlined color="primary" fontSize="small" />
                  <Typography variant="body2" color="text.secondary">Ready to explore?</Typography>
                </Box>

                <Button
                  variant="contained"
                  size="large"
                  startIcon={<Login />}
                  href="/login"
                  sx={{
                    px: 4,
                    borderRadius: 2,
                    fontWeight: 'bold',
                    boxShadow: 'none',
                    '&:hover': {
                      boxShadow: theme => `0 4px 12px ${theme.palette.primary.light + '40'}`
                    }
                  }}
                >
                  Go to Login
                </Button>
              </Box>
            </Box>
          </Box>
        ) : (
          <Formik
            initialValues={initialValues}
            validationSchema={RegisterSchema}
            onSubmit={handleSubmit}
          >
            {({ errors, touched }) => (
              <Form>
                <Stack spacing={2} mb={3}>
                  {[
                    { name: 'fullname', label: 'Full Name' },
                    { name: 'email', label: 'Root Email' },
                    { name: 'password', label: 'Password', type: 'password' },
                    { name: 'password_confirm', label: 'Confirm Password', type: 'password' },
                    { name: 'organization_name', label: 'Organization Name' },
                    { name: 'address', label: 'Address' },
                  ].map(({ name, label, type = 'text' }) => (
                    <Field
                      key={name}
                      as={TextField}
                      name={name}
                      label={label}
                      type={type}
                      fullWidth
                      variant="outlined"
                      size="small"
                      error={touched[name] && Boolean(errors[name])}
                      helperText={touched[name] && errors[name]}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                    />
                  ))}

                  <Grid2 container spacing={2}>
                    {[
                      { name: 'city', label: 'City' },
                      { name: 'state', label: 'State' },
                    ].map(({ name, label }) => (
                      <Grid2 key={name} item size={{ xs: 6 }}>
                        <Field
                          as={TextField}
                          name={name}
                          label={label}
                          fullWidth
                          variant="outlined"
                          size="small"
                          error={touched[name] && Boolean(errors[name])}
                          helperText={touched[name] && errors[name]}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                        />
                      </Grid2>
                    ))}
                  </Grid2>

                  <Field
                    as={TextField}
                    name="zip_code"
                    label="Zip Code"
                    fullWidth
                    variant="outlined"
                    size="small"
                    error={touched.zip_code && Boolean(errors.zip_code)}
                    helperText={touched.zip_code && errors.zip_code}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                  />

                  <Alert
                    variant="outlined"
                    severity="warning"
                    sx={{ borderRadius: 1.5 }}
                  >
                    <AlertTitle sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                      Registration Code Required
                    </AlertTitle>
                    <Typography variant="caption" color="text.secondary">
                      Please reach out to{' '}
                      <MuiLink href="mailto:Lag.webservices@gmail.com" underline="hover" fontWeight={500}>
                        Lag.webservices@gmail.com
                      </MuiLink>
                      {' '}to receive your code.
                    </Typography>
                  </Alert>

                  <Field
                    as={TextField}
                    name="code"
                    label="Registration Code"
                    fullWidth
                    variant="outlined"
                    size="small"
                    error={touched.code && Boolean(errors.code)}
                    helperText={touched.code && errors.code}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                  />
                </Stack>

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  sx={{
                    py: 1.5,
                    borderRadius: 1.5,
                    fontWeight: 'bold',
                    boxShadow: 'none',
                    '&:hover': {
                      boxShadow: theme => `0 4px 14px ${theme.palette.primary.main + '40'}`
                    }
                  }}
                >
                  Create Account
                </Button>

                {/* Sign in link */}
                <Box sx={{ textAlign: 'center', mt: 2.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    Already have an account?{' '}
                    <MuiLink
                      href="/login"
                      underline="hover"
                      fontWeight={600}
                      sx={{ color: 'primary.main' }}
                    >
                      Sign in
                    </MuiLink>
                  </Typography>
                </Box>
              </Form>
            )}
          </Formik>
        )}
      </Box>
    );
};

